import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import Footer from "../components/Footer";

const pages = {
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Effective Date: September 2026", 
    intro:
      "At Dwellora Homes, we respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, store, and protect your information when you visit our website, submit an enquiry, request an estimate, or use our services.",
    sections: [
      {
        heading: "Information We Collect",
        body: [
          "When you interact with Dwellora Homes, we may collect information such as:",
        ],
        list: [
          "Name",
          "Mobile number",
          "Email address",
          "Society or project name",
          "Plot details and dimensions",
          "Property location and project requirements",
          "Service preferences",
          "Construction or design requirements",
          "Information submitted through contact, consultation, or estimate forms",
        ],
        after:
          "We may also collect basic technical information such as browser type, device information, IP address, and website usage data for security and performance purposes.",
      },
      {
        heading: "How We Use Your Information",
        body: ["Your information may be used to:"], 
        list: [
          "Respond to your enquiries and requests",
          "Prepare and provide project estimates",
          "Understand your architectural, construction, or interior requirements",
          "Schedule consultations and site discussions",
          "Provide information about our services",
          "Coordinate project-related communication",
          "Improve our website, services, and customer experience",
          "Maintain website security and prevent misuse",
        ],
        after: "We do not sell your personal information to third parties.",
      },
      {
        heading: "Information Sharing",
        body: [
          "Your information may be shared with relevant members of our professional team when necessary to respond to your enquiry or deliver requested services.",
          "We may also disclose information when required by law, regulation, legal process, or government authority.",
        ],
      },
      {
        heading: "Data Security",
        body: [
          "We take reasonable technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or misuse.",
          "However, no method of electronic transmission or storage can be guaranteed to be completely secure.",
        ],
      },
      {
        heading: "Cookies",
        body: [
          "Our website may use cookies and similar technologies to improve functionality, understand website usage, and enhance your browsing experience.",  
        ],
        afterLink: { label: "Read our Cookies Policy", to: "/cookies-policy" }, 
      },
      {
        heading: "Third-Party Services",
        body: [
          "Our website may use third-party services for analytics, communication, hosting, forms, or other website functionality. These services may process information according to their own privacy policies.",
        ],
      },
      {
        heading: "Your Rights",
        body: ["You may contact us if you wish to:"],
        list: [
          "Request information about the personal data we hold about you",
          "Request correction of inaccurate information",
          "Ask about the use of your information",
          "Request deletion of information where applicable",
        ],
      },
      {
        heading: "Changes to This Policy",
        body: [
          "We may update this Privacy Policy from time to time to reflect changes in our services, technology, or applicable requirements.",
          "Any updated version will be published on this page with a revised effective date.",
        ],
      },
    ],
    contactTitle: "Contact Us",
    contactIntro:
      "If you have any questions regarding this Privacy Policy or the way your information is handled, please contact us.",
  },
  help: {
    title: "Help & Support",
    eyebrow: "Dwellora Homes support",
    intro:
      "Whether you are planning a new bungalow, looking for architectural design, need help with approvals, or want to understand construction costs, our team is here to guide you through the process.",
    sections: [
      {
        heading: "Planning to Build a Home?",
        body: [
          "Share your plot details, requirements, and approximate budget with us. Our team can help you understand the next steps for your project.",
        ],
      },
      {
        heading: "Need Architectural Design?",
        body: [
          "Our architecture services focus on customized planning based on your plot, orientation, lifestyle, family requirements, and design preferences.",
        ],
      },
      {
        heading: "Need Construction Services?",
        body: [
          "Dwellora provides bungalow construction and execution with planning, material management, site supervision, quality monitoring, and finishing works.",
        ],
      },
      {
        heading: "Need Building Approvals?",
        body: [
          "We assist with regulatory feasibility, sanction drawings, documentation, authority coordination, and approval-related processes for applicable municipal and PMRDA projects.",
        ],
      },
      {
        heading: "Want to Know Your Estimated Cost?",
        body: [
          "Use our Calculate My Cost tool to provide your plot or area details and select the services required for your project.",
        ],
        afterLink: { label: "Calculate My Cost", to: "/estimate" },
      },
      {
        heading: "Already Have Your Own Architect or Drawings?",
        body: [
          "Our team can discuss your existing plans and understand the scope of construction or execution support required.",
        ],
      },
      {
        heading: "Frequently Needed Information",
        body: [
          "Before contacting us, keeping the following details ready can help us understand your project faster:",
        ],
        list: [
          "Plot size or total construction area",
          "Plot location",
          "Road width",
          "Property approval/layout status",
          "Preferred type of construction",
          "Number of floors",
          "Approximate project requirements",
          "Your contact details",
        ],
      },
    ],
    contactTitle: "Need More Help?",
    contactIntro:
      "For questions about your project, estimate, design, construction, or approvals, contact our team directly.",
  },
  "cookies-policy": {
    title: "Cookies Policy",
    eyebrow: "Effective Date: September 2026",
    intro:
      "Dwellora Homes uses cookies and similar technologies to provide a better, more secure, and more efficient website experience.",
    sections: [
      {
        heading: "What Are Cookies?",
        body: [
          "Cookies are small text files stored on your device when you visit a website. They help websites remember information about your visit and understand how visitors interact with different pages.",
        ],
      },
      {
        heading: "How We Use Cookies",
        body: ["Dwellora Homes may use cookies to:"],
        list: [
          "Keep the website functioning correctly",
          "Remember certain preferences",
          "Understand website traffic and usage",
          "Improve website performance",
          "Maintain security",
          "Improve the overall user experience",
        ],
      },
      {
        heading: "Types of Cookies",
        body: [
          "Essential cookies are required for certain website functions and security features. Disabling them may affect website functionality.",
          "Analytics cookies may help us understand how visitors use our website, such as which pages are visited and how users interact with the website.",
          "Preference cookies may remember selected preferences to provide a more convenient browsing experience.",
        ],
      },
      {
        heading: "Third-Party Cookies",
        body: [
          "Some third-party services used by the website may place their own cookies or similar technologies on your device. Their use of cookies is governed by their respective privacy policies.",
        ],
      },
      {
        heading: "Managing Cookies",
        body: ["You can control or delete cookies through your browser settings. Most browsers allow you to:"],
        list: [
          "View stored cookies",
          "Delete existing cookies",
          "Block cookies",
          "Receive notifications before cookies are stored",
        ],
        after:
          "Please note that disabling certain cookies may affect some website functionality.",
      },
      {
        heading: "Updates to This Policy",
        body: [
          "We may update this Cookies Policy when our website, services, or technology changes.",
          "Any updates will be published on this page with a revised effective date.",
        ],
      },
    ],
    contactTitle: "Contact Us",
    contactIntro: "For questions regarding our use of cookies, contact us.",
  },
  credits: {
    title: "Credits",
    eyebrow: "Dwellora Homes",
    intro:
      "Dwellora Homes is a venture by Dwellora Designs LLP, bringing together architecture, approvals, construction, and design services under one professional team.",
    sections: [
      {
        heading: "Architecture & Construction",
        body: [
          "Dwellora Homes is led by an architecture-focused team with experience across residential design, construction, approvals, and project execution. The team combines architectural planning with practical construction expertise to deliver integrated solutions.",
        ],
      },
      {
        heading: "Website Design & Development",
        body: [
          "Website design and development, UI implementation, responsive experience, and digital functionality have been developed for Dwellora Homes to provide clients with a simple and informative way to explore our services, projects, and estimate options.",
        ],
      },
      {
        heading: "Photography & Visual Content",
        body: [
          "Project photographs, architectural visuals, interior imagery, and other visual assets displayed on the website are used to showcase Dwellora's design and construction work.",
          "Where applicable, third-party assets, tools, libraries, or services are used under their respective licenses and terms.",
        ],
      },
      {
        heading: "Technology",
        body: [
          "The website may use third-party technologies and services for functionality such as forms, analytics, communication, hosting, maps, and other website features.",
          "All respective trademarks, logos, names, and intellectual property belong to their respective owners.",
        ],
      },
      {
        heading: "A Note of Thanks",
        body: [
          "Thank you to our clients, architects, engineers, consultants, designers, construction teams, and partners who contribute to every Dwellora project.",
        ],
      },
    ],
    contactTitle: "Dwellora Homes",
    contactIntro: "A Venture by Dwellora Designs LLP",
  },
};

const tabs = [
  { label: "Privacy", to: "/privacy" },
  { label: "Help", to: "/help" },
  { label: "Cookies Policy", to: "/cookies-policy" },
  { label: "Credits", to: "/credits" },
];

function LegalPage({ openContactModal, showCTA }) {
  const location = useLocation();
  const pageSlug = location.pathname.replace(/^\/+/, "") || "privacy";
  const page = pages[pageSlug];

  if (!page) {
    return <Navigate to="/privacy" replace />;
  }

  return (
    <>
      <main className="dw-legal-page">
        <section className="dw-legal-hero">
          <div className="dw-legal-container">
            <p className="dw-legal-eyebrow">{page.eyebrow}</p>
            <h1>{page.title}</h1>
            <p className="dw-legal-intro">{page.intro}</p>

            <nav className="dw-legal-tabs" aria-label="Footer information pages">
              {tabs.map((tab) => (
                <Link
                  key={tab.to}
                  to={tab.to}
                  className={tab.to === `/${pageSlug}` ? "active" : ""}
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
          </div>
        </section>

        <section className="dw-legal-content">
          <div className="dw-legal-container">
            <div className="dw-legal-grid">
              {page.sections.map((section) => (
                <article className="dw-legal-card" key={section.heading}>
                  <h2>{section.heading}</h2>
                  {section.body?.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.list && (
                    <ul>
                      {section.list.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.after && <p>{section.after}</p>}
                  {section.afterLink && (
                    <Link className="dw-legal-inline-link" to={section.afterLink.to}>
                      {section.afterLink.label}
                    </Link>
                  )}
                </article>
              ))}
            </div>

            <aside className="dw-legal-contact">
              <div>
                <p className="dw-legal-contact-label">{page.contactTitle}</p>
                <p>{page.contactIntro}</p>
              </div>
              <div className="dw-legal-contact-details">
                <strong>Dwellora Homes</strong>
                <span>A Venture by Dwellora Designs LLP</span>
                <span>601, The Stellar Complex, Bhumkar Das Gugre Road, Wakad, Pune, Pimpri-Chinchwad, Maharashtra 411057</span>
                <a href="mailto:contact@dwellorahomes.com">contact@dwellorahomes.com</a>
                <a href="tel:+919156031516">+91 91560 31516</a>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer openContactModal={openContactModal} showCTA={showCTA} />
    </>
  );
}

export default LegalPage;
